import { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AWS from "aws-sdk";
import { Auth } from "aws-amplify";
import { Helmet } from "react-helmet-async";
import { SearchBar, Pagination, EmptyLayout } from "../../components/molecules";
import { selectUsers, selectUsersFilters, deleteUser } from "./index";
import { selectUser } from "../UserProfile";
import UserCard from "./UserCard";
import { ROUTES, ONE, DEFAULT_LIMIT, GITHUB_AUTH_FLOW } from "../../constants";
import { actions as appActions } from "../Applications/applications.reducers";
import { actions as userActions } from "../Users/users.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { usersIcon } from "../../assets/icons";
import classNames from "classnames";
import Spinner, {
  addSpinner,
  removeSpinner,
  SPINNER_NAMES,
} from "../../components/organisms/Spinner";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";

const { setSelectedUser, setUsersFilters } = userActions;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setShouldShowMenuBar } = appActions;
const { setSelectedTab } = breadCrumbActions;
const { GITHUB } = GITHUB_AUTH_FLOW;
const { DELETE_USER, FETCH_USERS } = SPINNER_NAMES;
const { USERROLES, INVITATIONS, LOGIN } = ROUTES;
const { MANAGE_USER_MODAL, DELETE_MODAL } = MODALS;

const {
  REACT_APP_AWS_REGION,
  REACT_APP_AWS_ACCESS_KEY_ID,
  REACT_APP_AWS_SECRET_ACCESS_KEY,
  REACT_APP_USER_POOL_ID,
} = process.env;

const cognito = new AWS.CognitoIdentityServiceProvider({
  region: REACT_APP_AWS_REGION,
  accessKeyId: REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: REACT_APP_AWS_SECRET_ACCESS_KEY,
});

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: usersData, count: usersCount, total: totalUsers } = useSelector(selectUsers);
  const userProfile = useSelector(selectUser);

  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [pageCount, setPageCount] = useState(ONE);
  const scrollRef = useRef();

  const currentPath = window.location.pathname;

  const filters = useSelector(selectUsersFilters);

  useEffect(() => {
    setPageCount(Math.ceil(usersCount / DEFAULT_LIMIT));
  }, [usersCount]);

  useEffect(() => {
    return () => {
      dispatch(setUsersFilters());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setShouldShowMenuBar(false));
    dispatch(setSelectedTab(null));
  }, [dispatch, currentPath]);

  useEffect(() => {
    scrollRef.current.scrollIntoView({ behavior: "smooth" });
  }, [filters]);

  const handleManageUser = useCallback(
    (user) => {
      dispatch(setSelectedModal(MANAGE_USER_MODAL));
      dispatch(setSelectedUser(user));
    },
    [dispatch]
  );

  const hideDeleteModal = useCallback(() => {
    setShouldShowDeleteModal(false);
    dispatch(setSelectedModal(null));
    dispatch(removeSpinner(DELETE_USER));
  }, [dispatch]);

  const handleSignOut = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.assign(LOGIN);
  };

  const handleDeleteUser = useCallback(
    (user) => {
      const cognitoParams = {
        UserPoolId: REACT_APP_USER_POOL_ID,
        Username: user.email,
      };
      dispatch(addSpinner(DELETE_USER));
      dispatch(deleteUser(user.id)).then(async (res) => {
        if (res.payload) {
          user.provider !== GITHUB &&
            (await cognito
              .adminDeleteUser(cognitoParams)
              .promise()
              .then(() =>
                user.id === userProfile.id
                  ? Promise.resolve(Auth.signOut()).then(() => handleSignOut())
                  : hideDeleteModal()
              ));
        }
      });
    },
    [dispatch, hideDeleteModal, userProfile.id]
  );

  const handleOnSearch = useCallback(
    (value) => {
      dispatch(setUsersFilters({ ...filters, name: value, page: ONE }));
    },
    [dispatch, filters]
  );

  const handleOnDeleteClick = (user) => {
    dispatch(setItemToBeDeleted(user));
    dispatch(setSelectedModal(DELETE_MODAL));
    setShouldShowDeleteModal(true);
  };

  const handlePagination = useCallback(
    (value) => {
      dispatch(setUsersFilters({ ...filters, page: value + ONE }));
    },
    [dispatch, filters]
  );
  const handleCreateNewUser = useCallback(() => {
    dispatch(setSelectedModal(MANAGE_USER_MODAL));
  }, [dispatch]);



  const renderUsers = () =>
    usersData.length ? (
      <div className="min-h-[61vh] mb-4">
        <div className="grid grid-cols-12 gap-4">
          {usersData &&
            usersData.map((user) => {
              return (
                  <UserCard
                    key={user.id}
                    user={user}
                    handleManageUser={handleManageUser}
                    handleOnDeleteClick={() => handleOnDeleteClick(user)}
                    setShouldShowDeleteModal={setShouldShowDeleteModal}
                  />
              );
            })}
        </div>
      </div>
    ) : (
      <EmptyLayout
        title="Welcome to Tava"
        subTitle="You can get started by invite new user."
        buttonText="Invite User"
        image={usersIcon}
        handleOnClick={handleCreateNewUser}
      />
    );

  return (
    <div className="scrollbar content overflow-auto flex-1">
      <Helmet>
        <title>Users | TavaJS</title>
      </Helmet>
      <div
        className="flex gap-6 flex-row items-center border-b border-gray-800 px-6 py-4 text-gray-400"
        ref={scrollRef}
      >
        <button className="rounded-lg py-2 px-4 text-sm font-medium bg-gray-800 text-gray-50">
          <span>Users</span>
        </button>
        <button
          className="rounded-lg py-2 px-4 hover:bg-gray-800 text-sm font-medium hover:text-white cursor-pointer"
          onClick={() => navigate(INVITATIONS)}
        >
          Invitations
        </button>
        <button
          className="rounded-lg py-2 px-4 hover:bg-gray-800 text-sm font-medium hover:text-white cursor-pointer"
          onClick={() => navigate(USERROLES)}
        >
          <span>User Roles</span>
        </button>
      </div>
      <div className="p-6">
        <div className={classNames("flex-col xs:flex-row flex justify-end mb-4 gap-4", { "justify-between": totalUsers > ONE })}>
          {totalUsers > ONE && (
            <SearchBar
              placeholder="Search"
              handleOnSearch={handleOnSearch}
            />
          )}
          <button
            type="button"
            className="btn btn-primary w-full xs:w-auto"
            onClick={() => dispatch(setSelectedModal(MANAGE_USER_MODAL))}
          >
            Invite User
          </button>
        </div>
      </div>
      <div className="h-2/3 p-6 -mt-[48px]">
        <Spinner name={FETCH_USERS}>
          {renderUsers()}
          <Pagination
            handlePagination={handlePagination}
            pagesCount={pageCount}
            activePage={filters.page - ONE}
            count={usersCount}
          />
        </Spinner>
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteUser}
            title="Are you sure you want to delete the User?"
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            spinner={DELETE_USER}
          />
        )}
      </div>
    </div>
  );
};
export default Users;
